import React, { Fragment, useState, useEffect, useContext, useRef } from 'react';
import TableBuilder, { FormOptionsBuilder, RegisteredInputsBuilder, ModalButtonBuilder, ButtonBuilder, ToastBuilder, HoverOver, VerticalButtonGroup } from '../../../../../../GlobalComponents/Builders';
import { buildOptionsFromArray, capitalizeFirstLetter, groupObjectsByKey, setInitialFormValues, sortArrayByKey, loadObjectToTextArea, parseJSONSafe, sortByKey, parseTextFromHTML, buildGroupedOptionsFromObject, buildSelectOptionsFromArray, sortArrayByKeys } from '../../../../../../GlobalComponents/Helpers';
import useDMTData from './hooks/useDMTData';
import useDMTHandlers from './hooks/useDMTHandlers';
import { Row, Card, CardBody, Form, Button } from 'reactstrap'
import { set, useForm } from 'react-hook-form';
import { Col } from 'react-bootstrap';
import { Btn, P } from '../../../../../../AbstractElements';
import { AccountContext } from '../../../../../../GlobalComponents/Authentication/Accounts';
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ContentLoader } from '../../../../../../Layout/Loader';
import moment from 'moment';
// DMT Sub-Components
import ResourceSection from './components/sections/resource/ResourceSection';
import ResourceMappingSection from './components/sections/resourceMapping/ResourceMappingSection';
import KeyMappingSection from './components/sections/keyMapping/KeyMappingSection';
import ValueMappingSection from './components/sections/valueMapping/ValueMappingSection';
import AssociationMappingSection from './components/sections/association/AssociationMappingSection';
import SupplementaryDataSection from './components/sections/supplementary/SupplementaryDataSection';
import SelectMappingsModal from './components/jobActions/SelectMappingsModal';
import ManageJobModal from './components/jobActions/ManageJobModal';
import ScheduleModal from './components/jobActions/ScheduleModal';
import RestrictAccessModal from './components/jobActions/RestrictAccessModal';
import CopySyncSettingsModal from './components/jobActions/CopySyncSettingsModal';
import GlobalSettingsSection from './components/jobSettings/GlobalSettingsSection';
import JobNotesSection from './components/jobSettings/JobNotesSection';
import CacheFieldsSection from './components/jobSettings/CacheFieldsSection';
import { Settings, FileText, Type, Globe, Clock, GitMerge, Database } from 'react-feather';
import TextPreviewModal from './components/toolbox/TextPreviewModal';
import ApiExplorerModal from './components/toolbox/ApiExplorer/ApiExplorerModal';
import TimestampConverterModal from './components/toolbox/TimestampConverterModal';
import JsonDiffModal from './components/toolbox/JsonDiffModal';

const DataMigrationToolJob = ({ jobData, getJobData }) => {

    const { jobId } = useParams();
    const navigate = useNavigate();

    // Seed-after-create: the create flow redirects here with ?seedSync=1&seedFrom=<id>
    // when "Seed from another sync" was chosen, so we auto-open the copy modal.
    const [seedSync, setSeedSync] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('seedSync') === '1'
            ? { from: params.get('seedFrom') || null }
            : null;
    });

    const clearSeedParam = () => {
        setSeedSync(null);
        // Strip the query params so a refresh doesn't re-trigger the modal.
        // Use history directly to avoid any router basename double-application.
        window.history.replaceState(null, '', window.location.pathname);
    };

    const { userSession, portalEmulation, isAdmin } = useContext(AccountContext);

    const mainForm = useForm();
    const { register, handleSubmit, getValues, control, setValue, formState: { errors, isValid }, resetField, watch } = mainForm;

    const { register: resourceRegister, handleSubmit: resourceHandleSubmit, control: resourceControl, setValue: resourceSetValue, formState: { errors: resourceErrors, isValid: resourceIsValid }, resetField: resourceResetField, watch: resourceWatch } = useForm();

    const actionForm = useForm();
    const { register: actionRegister, handleSubmit: actionHandleSubmit, getValues: actionGetValues, control: actionControl, setValue: actionSetValue, formState: { errors: actionErrors }, resetField: actionResetField, reset: actionReset } = actionForm;

    const {
        jobEventSource, jobEventDestination,
        mappingJobs, jobReport, allJobs,
        jobResources, mappedResources, jobResourceMappings,
        jobKeyMappings, jobKeyMappingsLength,
        showJobKeyMappings, setShowJobKeyMappings,
        showDeletedJobKeyMappings, setShowDeletedJobKeyMappings,
        keyMappingSummary,
        jobValueMappings, jobValueMappingsLength,
        showJobValueMappings, setShowJobValueMappings,
        valueMappingSummary,
        jobValueMappingDestinationUsage,
        supplementaryData, setSupplementaryData, supplementaryDataLength,
        showSupplementaryData, setShowSupplementaryData,
        compositeChains,
        availableResources,
        showResources, setShowResources,
        showResourceMappings, setShowResourceMappings,
        destinationConnectionKeys, allDestinationConnectionKeys,
        destinationConnectionValues, allDestinationConnectionValues,
        destinationResourceParentScoped,
        mappingCodes, mappingCodeOptions,
        dmtStates,
        dmtCompositeStates,
        isArchived, setIsArchived,
        defaultResourceMappings,
        renderPage, setRenderPage,
        showAccordionResource, setShowAccordionResource,
        dataPreview, setDataPreview,
        keyResourceLookup,
        isOpen, isOpen2, isOpen3,
        toggle, toggle2, toggle3,
        getMappingCodes, getDefaultResourceMappings,
        fetchDMTJobs, fetchResources, fetchKeyMappings,
        fetchDestinationResourceKeys, fetchSupplementaryData,
        fetchValueMappings, fetchDestinationResourceValues,
        fetchResourceMappings, fetchJobReport,
        getResourceKeyMappings, getResourceValueMappings,
        getJobMappings, getAllResourcesAndMappings, getEverything, getDMTMappingJobs,
        fetchAvailableResources,
    } = useDMTData(jobData, jobId);

    const {
        handleResourceUpdate, handleResourceCreate, handleXMLDownload, handleResourceDelete,
        handleJobAction, handleDMTAction,
        handleResourceMappingCreate, handleResourceMappingDelete,
        handleKeyCreate, handleSupplementaryValueCreate, handleSupplementaryValueDelete,
        handleValueCreate, handleDestinationValueCreate, handleMappingUpdate,
    } = useDMTHandlers({
        jobData, jobResources, jobEventSource, availableResources, isAdmin,
        resetField, resourceResetField,
        setShowJobKeyMappings, setShowJobValueMappings, setShowAccordionResource,
        fetchResources, fetchSupplementaryData, fetchResourceMappings,
        fetchKeyMappings, fetchValueMappings, fetchDestinationResourceValues,
        getResourceKeyMappings, getResourceValueMappings, getJobMappings, getAllResourcesAndMappings, getEverything,
    });


    const selectMappingsRef = useRef(null);
    const [selectMappingsLabel, setSelectMappingsLabel] = useState('Apply');

    const removeMappingOption = {
        'Remove Mapping': [{ label: 'Remove Existing Mapping 🚫', value: 0, name: 'BLANK' }]
    }

    const importMappingOption = {
        'System': [{ label: 'Import Value', value: -1, name: 'IMPORT' }]
    }

    useEffect(async () => {

        document.title = 'View Job'

        // setShowResources(false);

        if( portalEmulation.partnerId !== parseInt(jobData.partner_id) && portalEmulation.partnerId !== 0 ){
            ToastBuilder('error', 'You don\'t have access to view this page')
        } else {
            setIsArchived(jobData.is_archived)
            getEverything()

            document.title = isAdmin ? `DMT ${jobId} - ${jobData.name}` : `${jobData.name}`
        }

    }, []);

    if (renderPage) {

        return (
            <Fragment>
                <Row>
                    <Col xl="6">
                        <Card>
                            <CardBody>
                                <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 12 }}>
                                    <h4 style={{ margin: 0 }}>Job Details</h4>
                                    {!isArchived && (
                                        <div className='d-flex gap-2'>
                                            <GlobalSettingsSection jobData={jobData} isAdmin={isAdmin} isArchived={isArchived} trigger={
                                                <Settings size={22} className='icon-solid-info' style={{ cursor: 'pointer' }} title='Global Settings' />
                                            } />
                                            <JobNotesSection jobData={jobData} isAdmin={isAdmin} isArchived={isArchived} trigger={
                                                <FileText size={22} className='icon-solid-secondary' style={{ cursor: 'pointer' }} title='Job Notes' />
                                            } />
                                            {!!jobData.is_sync && <CacheFieldsSection jobData={jobData} isAdmin={isAdmin} isArchived={isArchived} trigger={
                                                <Database size={22} className='icon-solid-success' style={{ cursor: 'pointer' }} title='Cache Fields' />
                                            } />}
                                        </div>
                                    )}
                                </div>
                                <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Connections cannot be changed once selected.</p>
                                {jobData && jobData.name ? (<p><strong>Name: </strong>{' ' + jobData.name}</p>) : ''}
                                {jobData && jobData.source_connection ? (<p><strong>Source: </strong>{isAdmin ? <> <Link to={`${process.env.PUBLIC_URL}/connections/${jobData.source_connection.guid}`} target='_blank' style={{ color: 'inherit' }}>{jobData.source_connection.name}</Link> <strong>({jobData.source_connection.id})</strong></> : ' ' + jobData.source_connection.name}</p>) : ''}
                                {jobData && jobData.destination_connection ? (<p><strong>Destination: </strong>{isAdmin ? <> <Link to={`${process.env.PUBLIC_URL}/connections/${jobData.destination_connection.guid}`} target='_blank' style={{ color: (jobData.is_production ? 'red' : 'inherit') }}>{jobData.destination_connection.name}</Link> <strong>({jobData.destination_connection.id})</strong></> : <span style={{ color: (jobData.is_production ? 'red' : '') }}>{' ' + jobData.destination_connection.name}</span>}</p>) : ''}
                                {jobData && jobData.test_connection && jobData.test_connection.name ? (<p><strong>Test: </strong><span style={{ color: (jobData.is_production ? '' : 'green') }}>{' ' + jobData.test_connection.name}</span></p>) : ''}
                            </CardBody>
                        </Card>
                    </Col>
                    {showResources ?
                        <Col xl="6">
                            <Card>
                                <CardBody>
                                    <h4 style={{ marginBottom: 12 }}>Job Actions</h4>
                                    <div className='d-flex justify-content-between'>

                                        <div>
                                            <ButtonBuilder label='Dashboard' className='btn-primary m-1 px-4' color={null} onClick={() =>
                                                window.open(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${jobId}/progress`, '_blank')
                                            } />
                                            <ButtonBuilder label='Refresh' className='btn-secondary m-1 px-4' color={null} onClick={() => getEverything()} />

                                            {jobReport.length > 0 && !isArchived ?
                                                <ModalButtonBuilder title='Progress' className='btn-info m-1 px-4' color={null} size='xl'
                                                    body={
                                                        <Fragment>
                                                            <p>Need more details on the progress?
                                                                <Link style={{ color: 'green' }} to={`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${jobId}/progress/`}> Navigate to Progress Dashboard</Link>
                                                            </p>
                                                            <br />
                                                            <TableBuilder
                                                                data={jobReport}
                                                                processing={false}
                                                                pagination={false}
                                                                title=''
                                                                columns={[
                                                                    {
                                                                        name: 'Resource',
                                                                        selector: (row) => row.name,
                                                                        sortable: true,
                                                                        center: true,
                                                                    },
                                                                    {
                                                                        name: 'Staged',
                                                                        selector: (row) => row.staged,
                                                                        sortable: true,
                                                                        center: true,
                                                                    },
                                                                    {
                                                                        name: 'Queued',
                                                                        selector: (row) => row.queued,
                                                                        sortable: true,
                                                                        center: true,
                                                                    },
                                                                    {
                                                                        name: 'Errors',
                                                                        selector: (row) => row.create_error + row.update_error,
                                                                        sortable: true,
                                                                        center: true,
                                                                    },
                                                                    {
                                                                        name: 'Complete',
                                                                        selector: (row) => row.complete,
                                                                        sortable: true,
                                                                        center: true,
                                                                    }
                                                                ]}
                                                            />
                                                        </Fragment>
                                                    }
                                                /> : null}
                                        </div>

                                        <div>

                                            <VerticalButtonGroup label='Job Actions' className='btn-dark m-1'
                                                style={{ width: 170 }} buttons={[

                                                    isAdmin && !isArchived && jobData && jobData.is_sync && jobData.job_type === 'two-way-sync' ?
                                                        <CopySyncSettingsModal jobData={jobData} onComplete={() => getAllResourcesAndMappings()}
                                                            autoOpen={!!seedSync}
                                                            preselectSourceId={seedSync?.from}
                                                            onAutoOpenHandled={clearSeedParam} /> : null,

                                                    !isArchived && !(jobData?.is_sync && jobData?.job_type === 'two-way-sync') ?
                                                        <ModalButtonBuilder title='Select Menu' className='btn-outline-warning'
                                                            size='lg'
                                                            formId='apply-default-mappings-form'
                                                            onSubmitLabel={selectMappingsLabel}
                                                            onCancelLabel='Close'
                                                            body={
                                                                <SelectMappingsModal
                                                                    ref={selectMappingsRef}
                                                                    jobData={jobData}
                                                                    jobResources={jobResources}
                                                                    jobResourceMappings={jobResourceMappings}
                                                                    defaultResourceMappings={defaultResourceMappings}
                                                                    onComplete={() => getAllResourcesAndMappings()}
                                                                    onSelectionChange={(label) => setSelectMappingsLabel(label)}
                                                                />
                                                            }
                                                        /> : null,

                                                    isAdmin && jobData && jobData.is_sync && parseInt(jobData.sync_job_id) === 0 && jobData.job_type === 'two-way-sync' ?
                                                        <ModalButtonBuilder title='Link Job' className='btn-outline-danger'
                                                            onClick={() => fetchDMTJobs()}
                                                            body={
                                                                <>
                                                                    <p>Link this sync job to its corresponding sync</p>
                                                                    <br />
                                                                    <Form id='link-job-form' onSubmit={actionHandleSubmit((formData) => {
                                                                        if (!formData['linkSyncJobId']) {
                                                                            return ToastBuilder('error', 'Please make a Sync Job selection')
                                                                        }
                                                                        handleDMTAction('link-sync-job', null, () => getJobData(), formData)
                                                                    })}>
                                                                        <RegisteredInputsBuilder
                                                                            registrator={actionRegister}
                                                                            registratorErrors={actionErrors}
                                                                            inputs={[
                                                                                {
                                                                                    label: 'Sync Job',
                                                                                    type: 'select',
                                                                                    labelSize: 3,
                                                                                    options: <FormOptionsBuilder options={buildOptionsFromArray(allJobs, { label: 'name', value: 'job_id' })} />,
                                                                                    name: 'linkSyncJobId'
                                                                                }
                                                                            ]} />
                                                                    </Form>
                                                                    <p>There is <strong>NO</strong> way to revert this action.</p>
                                                                </>
                                                            }
                                                        /> : null,

                                                    jobData && parseInt(jobData.sync_job_id) > 0 ?
                                                        <ButtonBuilder label='Sync Job' className='btn-outline-secondary' onClick={() => window.open(`${process.env.PUBLIC_URL}/tools/${jobData.route}/jobs/${jobData.sync_job_id}`, '_blank')} /> : null,

                                                    !isArchived ?
                                                        <ScheduleModal jobData={jobData} jobResources={jobResources} jobResourceMappings={jobResourceMappings} handleDMTAction={handleDMTAction} dmtStates={dmtStates} dmtCompositeStates={dmtCompositeStates} /> : null,

                                                    isAdmin ?
                                                        <ModalButtonBuilder title='Restrict Access' className='btn-outline-dark' size='lg'
                                                            onSubmit={false}
                                                            onCancelLabel='Close'
                                                            body={
                                                                <RestrictAccessModal jobData={jobData} isArchived={isArchived} />
                                                            }
                                                        /> : null,

                                                    isAdmin && !isArchived ?
                                                        <ModalButtonBuilder title='Manage Job' className='btn-outline-danger' size='lg' style={{ width: 650 }}
                                                            onSubmit={false}
                                                            onCancelLabel='Close'
                                                            body={
                                                                <ManageJobModal
                                                                    jobData={jobData}
                                                                    jobResources={jobResources}
                                                                    jobResourceMappings={jobResourceMappings}
                                                                    jobKeyMappingsLength={jobKeyMappingsLength}
                                                                    jobValueMappingsLength={jobValueMappingsLength}
                                                                    handleDMTAction={handleDMTAction}
                                                                    getEverything={getEverything}
                                                                    getJobData={getJobData}
                                                                    onDeleteJob={() => {
                                                                        handleDMTAction('archive-job', null, () => {
                                                                            setRenderPage(false)
                                                                            handleDMTAction('delete-job');
                                                                            setTimeout(() => navigate(`${process.env.PUBLIC_URL}/tools/${jobData.route}`), 5000)
                                                                        }, null, true)
                                                                    }}
                                                                />
                                                            }
                                                        /> : null,

                                                    !isAdmin && !isArchived ?
                                                        <ModalButtonBuilder title='Delete Job' className='btn-outline-danger'
                                                            body={
                                                                <Form id='delete-job-form'
                                                                    onSubmit={
                                                                        actionHandleSubmit(() => {
                                                                            handleDMTAction('archive-job', null, () => {
                                                                                setRenderPage(false)
                                                                                handleDMTAction('delete-job');
                                                                                setTimeout(() => {
                                                                                    navigate(`${process.env.PUBLIC_URL}/tools/${jobData.route}`)
                                                                                }, 5000)
                                                                            }, null, true)
                                                                        })
                                                                    }>
                                                                    <p>This action will delete all data related to the job on this page.</p>
                                                                    <p>You should only delete this job if it was created out of error.</p>
                                                                    <p>Otherwise, we suggest archiving it.</p>
                                                                    <br />
                                                                    <p>There is <strong>NO</strong> way to revert this action.</p>
                                                                </Form>
                                                            }
                                                        /> : null

                                                ]} />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody className='py-3'>
                                    <div className='d-flex justify-content-between align-items-center'>
                                        <h6 style={{ margin: 0, fontSize: 13 }}>🧰 Toolbox</h6>
                                        <div className='d-flex gap-2'>
                                            <TextPreviewModal trigger={
                                                <Type size={18} className='icon-solid-success' style={{ cursor: 'pointer' }} title='Text Format Preview' />
                                            } />
                                            <ApiExplorerModal jobData={jobData} trigger={
                                                <Globe size={18} className='icon-solid-warning' style={{ cursor: 'pointer' }} title='API Explorer' />
                                            } />
                                            <TimestampConverterModal trigger={
                                                <Clock size={18} className='icon-solid-info' style={{ cursor: 'pointer' }} title='Timestamp Converter' />
                                            } />
                                            <JsonDiffModal trigger={
                                                <GitMerge size={18} className='icon-solid-secondary' style={{ cursor: 'pointer' }} title='JSON Diff Checker' />
                                            } />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        : null}
                </Row>
                <Row>
                    <ResourceSection
                        jobData={jobData}
                        jobResources={jobResources}
                        availableResources={availableResources}
                        fetchAvailableResources={fetchAvailableResources}
                        mappedResources={mappedResources}
                        showResources={showResources}
                        isArchived={isArchived}
                        isAdmin={isAdmin}
                        handleResourceCreate={handleResourceCreate}
                        handleResourceUpdate={handleResourceUpdate}
                        handleResourceDelete={handleResourceDelete}
                        resourceRegister={resourceRegister}
                        resourceHandleSubmit={resourceHandleSubmit}
                        resourceControl={resourceControl}
                        resourceSetValue={resourceSetValue}
                        resourceErrors={resourceErrors}
                    />
                </Row>
                <ResourceMappingSection
                    jobData={jobData}
                    jobEventSource={jobEventSource}
                    jobResources={jobResources}
                    jobResourceMappings={jobResourceMappings}
                    defaultResourceMappings={defaultResourceMappings}
                    showResources={showResources}
                    isArchived={isArchived}
                    isAdmin={isAdmin}
                    handleMappingUpdate={handleMappingUpdate}
                    handleResourceMappingCreate={handleResourceMappingCreate}
                    handleResourceMappingDelete={handleResourceMappingDelete}
                    handleDMTAction={handleDMTAction}
                    getAllResourcesAndMappings={getAllResourcesAndMappings}
                    getResourceKeyMappings={getResourceKeyMappings}
                    getResourceValueMappings={getResourceValueMappings}
                    register={register}
                    handleSubmit={handleSubmit}
                    errors={errors}
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    watch={watch}
                    actionRegister={actionRegister}
                    actionHandleSubmit={actionHandleSubmit}
                    actionErrors={actionErrors}
                    actionControl={actionControl}
                    actionReset={actionReset}
                    actionForm={actionForm}
                />

                <KeyMappingSection
                    jobKeyMappings={jobKeyMappings}
                    jobKeyMappingsLength={jobKeyMappingsLength}
                    showJobKeyMappings={showJobKeyMappings}
                    keyMappingSummary={keyMappingSummary}
                    showDeletedJobKeyMappings={showDeletedJobKeyMappings}
                    setShowDeletedJobKeyMappings={setShowDeletedJobKeyMappings}
                    isOpen={isOpen}
                    toggle={toggle}
                    mappingCodes={mappingCodes}
                    mappingCodeOptions={mappingCodeOptions}
                    showAccordionResource={showAccordionResource}
                    destinationConnectionKeys={destinationConnectionKeys}
                    allDestinationConnectionKeys={allDestinationConnectionKeys}
                    supplementaryData={supplementaryData}
                    removeMappingOption={removeMappingOption}
                    isArchived={isArchived}
                    isAdmin={isAdmin}
                    handleMappingUpdate={handleMappingUpdate}
                    register={register}
                    handleSubmit={handleSubmit}
                    errors={errors}
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    watch={watch}
                    jobData={jobData}
                    fetchKeyMappings={fetchKeyMappings}
                    fetchDestinationResourceKeys={fetchDestinationResourceKeys}
                    actionRegister={actionRegister}
                    actionHandleSubmit={actionHandleSubmit}
                    actionErrors={actionErrors}
                    actionControl={actionControl}
                    actionReset={actionReset}
                    actionSetValue={actionSetValue}
                    actionForm={actionForm}
                    handleKeyCreate={handleKeyCreate}
                    handleDMTAction={handleDMTAction}
                    mappingJobs={mappingJobs}
                    dataPreview={dataPreview}
                    setDataPreview={setDataPreview}
                    getResourceKeyMappings={getResourceKeyMappings}
                    jobValueMappings={jobValueMappings}
                    jobResourceMappings={jobResourceMappings}
                    dmtStates={dmtStates}
                />

                <ValueMappingSection
                    jobData={jobData}
                    jobValueMappings={jobValueMappings}
                    jobValueMappingsLength={jobValueMappingsLength}
                    showJobValueMappings={showJobValueMappings}
                    valueMappingSummary={valueMappingSummary}
                    jobValueMappingDestinationUsage={jobValueMappingDestinationUsage}
                    isOpen2={isOpen2}
                    toggle2={toggle2}
                    showAccordionResource={showAccordionResource}
                    destinationConnectionValues={destinationConnectionValues}
                    allDestinationConnectionValues={allDestinationConnectionValues}
                    destinationResourceParentScoped={destinationResourceParentScoped}
                    removeMappingOption={removeMappingOption}
                    isArchived={isArchived}
                    isAdmin={isAdmin}
                    handleMappingUpdate={handleMappingUpdate}
                    handleValueCreate={handleValueCreate}
                    handleDestinationValueCreate={handleDestinationValueCreate}
                    handleDMTAction={handleDMTAction}
                    handleJobAction={handleJobAction}
                    handleXMLDownload={handleXMLDownload}
                    getResourceValueMappings={getResourceValueMappings}
                    fetchValueMappings={fetchValueMappings}
                    fetchDestinationResourceValues={fetchDestinationResourceValues}
                    jobResourceMappings={jobResourceMappings}
                    register={register}
                    handleSubmit={handleSubmit}
                    errors={errors}
                    control={control}
                    setValue={setValue}
                    actionHandleSubmit={actionHandleSubmit}
                    actionRegister={actionRegister}
                    actionErrors={actionErrors}
                    actionControl={actionControl}
                    actionReset={actionReset}
                    actionSetValue={actionSetValue}
                    jobKeyMappings={jobKeyMappings}
                    keyResourceLookup={keyResourceLookup}
                    dmtStates={dmtStates}
                />

                <AssociationMappingSection jobData={jobData} jobResourceMappings={jobResourceMappings} />

                <SupplementaryDataSection
                    jobData={jobData}
                    supplementaryData={supplementaryData}
                    setSupplementaryData={setSupplementaryData}
                    supplementaryDataLength={supplementaryDataLength}
                    showSupplementaryData={showSupplementaryData}
                    compositeChains={compositeChains}
                    isArchived={isArchived}
                    handleSupplementaryValueCreate={handleSupplementaryValueCreate}
                    handleSupplementaryValueDelete={handleSupplementaryValueDelete}
                    handleDMTAction={handleDMTAction}
                    fetchSupplementaryData={fetchSupplementaryData}
                    form={mainForm}
                />

                <div className="text-center mt-4 p-3 border-top">
                    <small className="text-muted">
                        🛠️ Data Migration Tool · v3.0
                    </small>
                </div>
            </Fragment>
        )
    }
    else {
        if( portalEmulation.partnerId !== parseInt(jobData.partner_id) && portalEmulation.partnerId !== 0 ){
            return <Navigate to={`${process.env.PUBLIC_URL}/dashboard`} />
        }
        return (<Fragment><ContentLoader isLoading={!renderPage} /></Fragment>)
    }
};

export default DataMigrationToolJob;
